import React, { ReactNode } from 'react';
import { useEntitlement } from '../hooks/useEntitlement';
import { EntitlementErrorBoundary } from './EntitlementErrorBoundary';

interface PremiumGateProps {
  children: ReactNode;
  fallback: ReactNode;
  loadingComponent?: ReactNode;
  showErrorDetails?: boolean;
}

/**
 * Component that gates premium content based on user entitlement
 * Shows loading state instead of defaulting to free to prevent flicker
 * Wrapped in EntitlementErrorBoundary for graceful error handling
 */
export const PremiumGate: React.FC<PremiumGateProps> = ({ 
  children, 
  fallback, 
  loadingComponent,
  showErrorDetails = false
}) => {
  return (
    <EntitlementErrorBoundary
      // On an entitlement error, show the warning plus the free view (fallback),
      // never the premium children: we can't confirm the user is premium.
      fallback={(entitlementError, resetError, suggestions) => (
        <>
        <div className="mb-4 p-4 border border-yellow-200 dark:border-yellow-800 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-yellow-600 dark:text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Unable to verify premium status
              </h3>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                {entitlementError.userMessage}
              </p>
              
              {showErrorDetails && suggestions.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    Suggestions:
                  </p>
                  <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                    {suggestions.slice(0, 2).map((suggestion, index) => (
                      <li key={index} className="flex items-center gap-1">
                        <span className="w-1 h-1 bg-yellow-600 dark:bg-yellow-400 rounded-full flex-shrink-0"></span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  onClick={resetError}
                  className="text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 underline"
                >
                  Try Again
                </button>
              </div>
              
              <div className="mt-3 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-700 dark:text-yellow-300">
                Showing free content as a precaution. Your premium features may still be available.
              </div>
            </div>
          </div>
        </div>
        {fallback}
        </>
      )}
    >
      <PremiumGateContent 
        children={children}
        fallback={fallback}
        loadingComponent={loadingComponent}
      />
    </EntitlementErrorBoundary>
  );
};

/**
 * Internal component that handles the actual premium gating logic
 * Separated to work properly with error boundary
 */
const PremiumGateContent: React.FC<{
  children: ReactNode;
  fallback: ReactNode;
  loadingComponent?: ReactNode;
}> = ({ children, fallback, loadingComponent }) => {
  const { is_premium, loading, error, entitlementError } = useEntitlement();

  // Show loading state while fetching entitlement
  if (loading) {
    return (
      <>
        {loadingComponent || (
          <div className="flex items-center justify-center p-4">
            <div className="animate-pulse flex space-x-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show error state if entitlement fetch failed
  if (error || entitlementError) {
    console.error('Premium gate error:', error || entitlementError);
    
    // Throw error to be caught by EntitlementErrorBoundary
    if (error) {
      throw error;
    }
    
    // Default to showing fallback on error to be safe
    return <>{fallback}</>;
  }

  // Show premium content or fallback based on entitlement
  return <>{is_premium ? children : fallback}</>;
};